import React, { useCallback, useEffect, useState } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { alpha } from "@mui/material/styles";
import { Box, CircularProgress, Fade, Paper, Typography } from "@mui/material";
import TouchApp from "@mui/icons-material/TouchApp";
import { loadNearbyZipCodes } from "components/dashboard/zipSlotAds/zipCodes";
import ZipMarker from "components/dashboard/zipSlotAds/ZipMarker";
import "leaflet/dist/leaflet.css";
import { useFacility } from "hooks/useFacility";

const FIXED_RADIUS = 50;
const MAX_MARKERS = 150;

/**
 * Zip-selection map for zip-slot CPC bidding (nearby ZIPs + markers).
 * Does not implement auction-cycle lockouts.
 */
const ZipSlotZipMap = ({ selectedZips, onZipToggle, ownBidZips = new Set() }) => {
  const { data: facility } = useFacility();
  const [loading, setLoading] = useState(true);
  const [visibleMarkers, setVisibleMarkers] = useState([]);
  useEffect(() => {
    if (!facility?.lat || !facility?.lng) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const selectedZipArray = Array.from(selectedZips || []);
    const loadZipCodes = async () => {
      setLoading(true);
      try {
        const zipCodes = await loadNearbyZipCodes(
          facility.lat,
          facility.lng,
          FIXED_RADIUS,
        );
        if (cancelled) return;

        const priorityZips = selectedZipArray
          .filter((zipId) => zipCodes[zipId])
          .map((zipId) => zipCodes[zipId]);

        const otherZips = Object.values(zipCodes)
          .filter((zip) => !selectedZipArray.includes(zip.id))
          .filter((zip) => zip.lat && zip.lng)
          .sort((a, b) => {
            const distA = Math.hypot(a.lat - facility.lat, a.lng - facility.lng);
            const distB = Math.hypot(b.lat - facility.lat, b.lng - facility.lng);
            return distA - distB;
          })
          .slice(0, MAX_MARKERS - priorityZips.length);

        setVisibleMarkers([...priorityZips, ...otherZips]);
      } catch (error) {
        console.error("Error loading ZIP codes:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadZipCodes();
    return () => {
      cancelled = true;
    };
  }, [facility, selectedZips]);

  const handleZipToggle = useCallback(
    (zipCode) => {
      if (onZipToggle) onZipToggle(zipCode);
    },
    [onZipToggle],
  );

  if (!facility?.lat || !facility?.lng) {
    return null;
  }

  const showMessage =
    !loading && (visibleMarkers.length === 0 || selectedZips.size === 0);

  return (
    <Box sx={{ mb: 4, height: 400, position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ height: "100%", width: "100%" }}>
        <MapContainer
          center={[facility.lat, facility.lng]}
          zoom={12}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url={`https://api.maptiler.com/maps/streets/{z}/{x}/{y}.png?key=${
              process.env.REACT_APP_MAPTILER_KEY || ""
            }`}
            attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
          />

          {visibleMarkers.map((zip) => (
            <ZipMarker
              key={zip.id}
              zip={zip}
              isSelected={selectedZips.has(zip.id)}
              hasActiveAd={ownBidZips.has(zip.id)}
              hasActivePromotion={false}
              onToggle={handleZipToggle}
            />
          ))}

          <Fade in={showMessage} timeout={500}>
            <Box
              sx={{
                position: "absolute",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 500,
                pointerEvents: "none",
              }}
            >
              <Paper
                variant="elevation"
                elevation={2}
                sx={{ px: 2, py: 1.5, textAlign: "center" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TouchApp
                    sx={{ fontSize: 20, color: "primary.main", opacity: 0.8 }}
                  />
                  <Typography variant="body2" fontWeight={500}>
                    Click on a marker to select a ZIP code.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Fade>
        </MapContainer>
      </Box>
    </Box>
  );
};

export default ZipSlotZipMap;
